import { useDataLookups } from 'data/data-lookup';
import moment from 'moment';
import Papa from 'papaparse';
import React from 'react';
import { toast } from 'sonner';
import {
  EDataLookupFileKind,
  EDataLookupStatus,
  IAPNLookupColumnMapping,
  IDataLookup,
} from 'types/data-lookup';

import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloudUploadRoundedIcon from '@material-ui/icons/CloudUploadRounded';
import DescriptionRoundedIcon from '@material-ui/icons/DescriptionRounded';
import GetAppRoundedIcon from '@material-ui/icons/GetAppRounded';
import HistoryRoundedIcon from '@material-ui/icons/HistoryRounded';
import HomeWorkRoundedIcon from '@material-ui/icons/HomeWorkRounded';
import PeopleAltRoundedIcon from '@material-ui/icons/PeopleAltRounded';
import SearchRoundedIcon from '@material-ui/icons/SearchRounded';

import { downloadDataLookupFile, submitAPNLookup } from 'utils/api/data-lookups';

const MAX_FILE_SIZE = 15 * 1024 * 1024;
const MAX_INPUT_RECORDS = 2500;
const DATA_LOOKUP_ERROR_MESSAGE = 'Unable to process this data lookup.';
const isAuditFile = (kind: EDataLookupFileKind) => kind === EDataLookupFileKind.Audit;

const emptyMapping: IAPNLookupColumnMapping = {
  address: '', city: '', state: '', zip: '',
};

const mappingFields: {
  key: keyof IAPNLookupColumnMapping;
  label: string;
  required: boolean;
  aliases: string[];
}[] = [
  {
    key: 'address', label: 'Property address', required: true, aliases: ['propertyaddress', 'siteaddress', 'postaladdress', 'streetaddress', 'address'],
  },
  {
    key: 'city', label: 'Property city', required: true, aliases: ['propertycity', 'sitecity', 'postalcity', 'city'],
  },
  {
    key: 'state', label: 'Property state', required: true, aliases: ['propertystate', 'sitestate', 'postalstate', 'state'],
  },
  {
    key: 'zip', label: 'Property ZIP', required: true, aliases: ['propertyzip', 'propertyzipcode', 'sitezip', 'sitezipcode', 'postalzip', 'postalcode', 'zipcode', 'zip'],
  },
  {
    key: 'firstName', label: 'First name', required: false, aliases: ['firstname', 'ownerfirstname', 'owner1first', 'owner1firstname'],
  },
  {
    key: 'lastName', label: 'Last name', required: false, aliases: ['lastname', 'ownerlastname', 'owner1last', 'owner1lastname'],
  },
  {
    key: 'email', label: 'Email address', required: false, aliases: ['emailaddress', 'owneremail', 'email'],
  },
  {
    key: 'phone', label: 'Phone number', required: false, aliases: ['phonenumber', 'ownerphone', 'phone'],
  },
];

const normalizeHeader = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');

const suggestMapping = (headers: string[]): IAPNLookupColumnMapping => {
  const normalizedHeaders = headers.map((header) => ({ header, normalized: normalizeHeader(header) }));
  return mappingFields.reduce((mapping, field) => {
    const exactMatch = normalizedHeaders.find(({ normalized }) => field.aliases.includes(normalized));
    const partialMatch = exactMatch ?? normalizedHeaders.find(({ normalized }) => (
      field.aliases.some((alias) => normalized.includes(alias))
    ));
    return partialMatch ? { ...mapping, [field.key]: partialMatch.header } : mapping;
  }, { ...emptyMapping });
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    marginTop: theme.spacing(3),
    paddingBottom: theme.spacing(4),
  },
  card: {
    height: 'auto',
    overflow: 'hidden',
    margin: '0 !important',
    padding: '0 !important',
    border: `1px solid ${theme.palette.grayScale6.main}`,
    borderRadius: 16,
    backgroundColor: theme.palette.white.main,
    boxShadow: '0 10px 30px rgba(23, 23, 24, 0.045)',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(2.25, 3),
    borderBottom: `1px solid ${theme.palette.grayScale6.main}`,
    background: 'linear-gradient(135deg, rgba(150, 214, 179, 0.20) 0%, rgba(246, 246, 247, 0.72) 100%)',
    [theme.breakpoints.down('xs')]: {
      alignItems: 'flex-start',
      padding: theme.spacing(2, 2.25),
    },
  },
  cardHeading: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  cardIcon: {
    width: 42,
    height: 42,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: theme.palette.primary.dark,
    backgroundColor: theme.palette.white.main,
    border: '1px solid rgba(86, 145, 127, 0.18)',
    borderRadius: 12,
    boxShadow: '0 4px 12px rgba(60, 85, 71, 0.08)',
    '& svg': { fontSize: 21 },
  },
  cardTitle: {
    color: theme.palette.darkGray.main,
    fontSize: 17,
    fontWeight: 700,
    lineHeight: 1.35,
  },
  cardDescription: {
    color: theme.palette.text.secondary,
    fontSize: 14,
    lineHeight: 1.55,
    marginTop: 3,
  },
  cardBody: {
    padding: theme.spacing(3),
    '&:last-child': { paddingBottom: theme.spacing(3) },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2.25),
      '&:last-child': { paddingBottom: theme.spacing(2.25) },
    },
  },
  historyCount: {
    flexShrink: 0,
    padding: theme.spacing(0.65, 1.25),
    color: theme.palette.primary.dark,
    backgroundColor: 'rgba(255, 255, 255, 0.76)',
    border: '1px solid rgba(86, 145, 127, 0.18)',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    [theme.breakpoints.down('xs')]: { display: 'none' },
  },
  uploadZone: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 240,
    padding: theme.spacing(4),
    textAlign: 'center',
    border: '1.5px dashed rgba(86, 145, 127, 0.38)',
    borderRadius: 14,
    background: 'linear-gradient(180deg, rgba(246, 250, 248, 0.72) 0%, rgba(250, 250, 250, 0.95) 100%)',
    transition: 'border-color 160ms ease, background-color 160ms ease, box-shadow 160ms ease',
    [theme.breakpoints.down('xs')]: {
      minHeight: 220,
      padding: theme.spacing(3, 2),
    },
  },
  uploadZoneDragging: {
    borderColor: theme.palette.primary.main,
    background: 'rgba(150, 214, 179, 0.15)',
    boxShadow: 'inset 0 0 0 1px rgba(86, 145, 127, 0.12)',
  },
  uploadIcon: {
    width: 58,
    height: 58,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.dark,
    backgroundColor: theme.palette.white.main,
    border: '1px solid rgba(86, 145, 127, 0.16)',
    borderRadius: 16,
    boxShadow: '0 8px 22px rgba(60, 85, 71, 0.10)',
    '& svg': { fontSize: 28 },
  },
  uploadTitle: {
    color: theme.palette.darkGray.main,
    fontSize: 16,
    fontWeight: 700,
  },
  uploadHelp: {
    maxWidth: 470,
    margin: theme.spacing(0.75, 0, 2.25),
    color: theme.palette.text.secondary,
    fontSize: 14,
    lineHeight: 1.6,
  },
  uploadMeta: {
    marginTop: theme.spacing(1.25),
    color: theme.palette.text.secondary,
    fontSize: 13,
  },
  primaryButton: {
    '&.MuiButton-root': {
      margin: 0,
      padding: '11px 24px',
      borderRadius: 24,
      fontSize: 13,
      fontWeight: 700,
      textTransform: 'none',
      boxShadow: '0 5px 14px rgba(86, 145, 127, 0.18)',
      '& svg': { marginRight: theme.spacing(0.75) },
    },
  },
  fileCard: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 1.75),
    border: `1px solid ${theme.palette.grayScale6.main}`,
    borderRadius: 12,
    backgroundColor: theme.palette.grayScale8.main,
  },
  fileIcon: {
    width: 38,
    height: 38,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: theme.palette.primary.dark,
    backgroundColor: theme.palette.white.main,
    border: `1px solid ${theme.palette.grayScale6.main}`,
    borderRadius: 10,
    '& svg': { fontSize: 20 },
  },
  fileDetails: {
    minWidth: 0,
    flex: 1,
  },
  fileName: {
    overflow: 'hidden',
    color: theme.palette.darkGray.main,
    fontSize: 13,
    fontWeight: 700,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  fileMeta: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    lineHeight: 1.5,
    marginTop: 3,
  },
  replaceButton: {
    flexShrink: 0,
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'none',
  },
  mappingGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(2.5),
    marginTop: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  mappingSection: {
    minWidth: 0,
    padding: theme.spacing(2.25),
    border: `1px solid ${theme.palette.grayScale6.main}`,
    borderRadius: 14,
    backgroundColor: theme.palette.white.main,
  },
  mappingSectionHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.25),
    marginBottom: theme.spacing(3),
  },
  mappingSectionIcon: {
    width: 34,
    height: 34,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: theme.palette.primary.dark,
    backgroundColor: 'rgba(150, 214, 179, 0.16)',
    borderRadius: 9,
    '& svg': { fontSize: 18 },
  },
  mappingSectionTitle: {
    color: theme.palette.darkGray.main,
    fontSize: 14,
    fontWeight: 700,
  },
  mappingSectionDescription: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    lineHeight: 1.55,
    marginTop: 3,
  },
  fieldsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    columnGap: theme.spacing(2.5),
    rowGap: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: '1fr',
      rowGap: theme.spacing(2.5),
    },
  },
  fieldControl: {
    minWidth: 0,
    '& .MuiOutlinedInput-root': { backgroundColor: theme.palette.grayScale8.main },
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2.5),
    marginTop: theme.spacing(3),
    paddingTop: theme.spacing(2.5),
    borderTop: `1px solid ${theme.palette.grayScale6.main}`,
    [theme.breakpoints.down('xs')]: {
      alignItems: 'stretch',
      flexDirection: 'column',
    },
  },
  actionButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    [theme.breakpoints.down('xs')]: {
      '& > button, & > label': { flex: 1 },
    },
  },
  clearButton: {
    '&.MuiButton-root:hover': { backgroundColor: 'transparent' },
    '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.primary.dark}`, outlineOffset: 2 },
  },
  uploadProgress: {
    flex: 1,
    maxWidth: 360,
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 10,
    backgroundColor: theme.palette.grayScale8.main,
    [theme.breakpoints.down('xs')]: { maxWidth: 'none' },
  },
  progressLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(0.75),
    color: theme.palette.text.secondary,
    fontSize: 11,
    fontWeight: 600,
  },
  tableContainer: {
    overflowX: 'auto',
    boxShadow: 'none',
    '& .MuiTableCell-head': {
      color: theme.palette.text.secondary,
      backgroundColor: theme.palette.grayScale8.main,
      borderBottom: `1px solid ${theme.palette.grayScale6.main}`,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
    },
    '& .MuiTableCell-body': {
      color: theme.palette.darkGray.main,
      borderBottom: `1px solid ${theme.palette.grayScale6.main}`,
      fontSize: 12,
    },
    '& .MuiTableRow-root:last-child .MuiTableCell-body': { borderBottom: 0 },
    '& .MuiTableRow-root:hover .MuiTableCell-body': { backgroundColor: 'rgba(246, 246, 247, 0.45)' },
  },
  historyBody: {
    padding: '0 !important',
  },
  inputFileCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 190,
    fontWeight: 600,
    '& svg': { color: theme.palette.primary.dark, fontSize: 18, flexShrink: 0 },
  },
  progressCell: {
    minWidth: 160,
  },
  rowProgress: {
    marginTop: theme.spacing(0.75),
  },
  downloadActions: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    minWidth: 130,
    '& .MuiButton-root': {
      borderRadius: 8,
      fontSize: 11,
      fontWeight: 700,
      textTransform: 'none',
    },
  },
  statusChip: { border: '1px solid currentColor', fontWeight: 700 },
  requestedChip: { backgroundColor: '#fff7ed', color: '#9a3412', borderColor: '#fed7aa' },
  processingChip: { backgroundColor: '#eff6ff', color: '#1d4ed8', borderColor: '#bfdbfe' },
  completedChip: { backgroundColor: '#ecfdf5', color: '#047857', borderColor: '#a7f3d0' },
  failedChip: { backgroundColor: '#fef2f2', color: '#b91c1c', borderColor: '#fecaca' },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(6, 3),
    textAlign: 'center',
  },
  emptyIcon: {
    width: 52,
    height: 52,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(1.5),
    color: theme.palette.primary.dark,
    backgroundColor: 'rgba(150, 214, 179, 0.16)',
    borderRadius: 14,
    '& svg': { fontSize: 25 },
  },
  emptyTitle: {
    color: theme.palette.darkGray.main,
    fontSize: 14,
    fontWeight: 700,
  },
  emptyDescription: {
    maxWidth: 390,
    marginTop: theme.spacing(0.75),
    color: theme.palette.text.secondary,
    fontSize: 14,
    lineHeight: 1.6,
  },
  pagination: {
    borderTop: `1px solid ${theme.palette.grayScale6.main}`,
  },
}));

type LookupCardHeaderProps = {
  icon: React.ReactNode;
  title: string;
  description: string;
  action?: React.ReactNode;
};

const LookupCardHeader: React.FC<LookupCardHeaderProps> = ({
  icon, title, description, action,
}) => {
  const classes = useStyles();
  return (
    <div className={classes.cardHeader}>
      <div className={classes.cardHeading}>
        <span className={classes.cardIcon} aria-hidden="true">{icon}</span>
        <div>
          <Typography component="h2" className={classes.cardTitle}>{title}</Typography>
          <Typography component="p" className={classes.cardDescription}>{description}</Typography>
        </div>
      </div>
      {action}
    </div>
  );
};

const statusLabels: Record<EDataLookupStatus, string> = {
  [EDataLookupStatus.Requested]: 'Requested',
  [EDataLookupStatus.Processing]: 'Processing',
  [EDataLookupStatus.Completed]: 'Completed',
  [EDataLookupStatus.Failed]: 'Failed',
};

const APNLookupView = () => {
  const classes = useStyles();
  const [file, setFile] = React.useState<File>();
  const [headers, setHeaders] = React.useState<string[]>([]);
  const [mapping, setMapping] = React.useState<IAPNLookupColumnMapping>(emptyMapping);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isDragging, setIsDragging] = React.useState(false);
  const [uploadProgress, setUploadProgress] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const {
    dataLookups, pagination, isLoading, mutate,
  } = useDataLookups(page + 1, rowsPerPage);

  const resetUpload = () => {
    setFile(undefined);
    setHeaders([]);
    setMapping(emptyMapping);
    setUploadProgress(0);
  };

  const processSelectedFile = (selectedFile?: File) => {
    if (!selectedFile) return;
    if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please choose a CSV file.');
      return;
    }
    if (selectedFile.size > MAX_FILE_SIZE) {
      toast.error('CSV files must be 15 MB or smaller.');
      return;
    }

    Papa.parse<Record<string, string>>(selectedFile, {
      header: true,
      transformHeader: (header) => header.trim(),
      preview: MAX_INPUT_RECORDS + 1,
      skipEmptyLines: true,
      complete: (results) => {
        const parsedHeaders = results.meta.fields ?? [];
        if (!parsedHeaders.length || !results.data.length) {
          toast.error('The CSV must contain headers and at least one data row.');
          return;
        }
        if (results.data.length > MAX_INPUT_RECORDS) {
          toast.error(`CSV files can contain up to ${MAX_INPUT_RECORDS.toLocaleString()} records.`);
          return;
        }
        setFile(selectedFile);
        setHeaders(parsedHeaders);
        setMapping(suggestMapping(parsedHeaders));
      },
      error: () => toast.error('The CSV could not be read.'),
    });
  };

  const onFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    processSelectedFile(input.files?.[0]);
    input.value = '';
  };

  const onFileDropped = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    if (!isSubmitting) processSelectedFile(event.dataTransfer.files?.[0]);
  };

  const requiredMappingsComplete = mappingFields
    .filter(({ required }) => required)
    .every(({ key }) => !!mapping[key]);

  const onSubmit = async () => {
    if (!file || !requiredMappingsComplete) return;
    setIsSubmitting(true);
    setUploadProgress(0);
    try {
      await submitAPNLookup(file, mapping, setUploadProgress);
      toast.success('APN lookup submitted. You can leave this page while it processes.');
      resetUpload();
      setPage(0);
      await mutate();
    } catch (err: any) {
      toast.error(err?.response?.data?.error ?? DATA_LOOKUP_ERROR_MESSAGE);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onDownload = async (lookup: IDataLookup, outputFile: IDataLookup['outputFiles'][number]) => {
    try {
      await downloadDataLookupFile(lookup._id, outputFile);
    } catch (err) {
      toast.error('The output file could not be downloaded.');
    }
  };

  const statusClass = (status: EDataLookupStatus) => ({
    [EDataLookupStatus.Requested]: classes.requestedChip,
    [EDataLookupStatus.Processing]: classes.processingChip,
    [EDataLookupStatus.Completed]: classes.completedChip,
    [EDataLookupStatus.Failed]: classes.failedChip,
  }[status]);

  return (
    <div className={classes.root}>
      <Card variant="outlined" className={classes.card}>
        <LookupCardHeader
          icon={<CloudUploadRoundedIcon />}
          title="Create an APN Lookup"
          description="Upload property records and map the columns used to find parcel numbers"
        />
        <CardContent className={classes.cardBody}>
          <form onSubmit={(event) => { event.preventDefault(); onSubmit(); }}>
            {!file ? (
              <div
                className={`${classes.uploadZone} ${isDragging ? classes.uploadZoneDragging : ''}`}
                onDragEnter={(event) => { event.preventDefault(); setIsDragging(true); }}
                onDragOver={(event) => event.preventDefault()}
                onDragLeave={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget as Node)) setIsDragging(false);
                }}
                onDrop={onFileDropped}
              >
                <span className={classes.uploadIcon} aria-hidden="true"><CloudUploadRoundedIcon /></span>
                <Typography component="h3" className={classes.uploadTitle}>Upload your property CSV</Typography>
                <Typography component="p" className={classes.uploadHelp}>
                  Drag and drop a file here, or choose one from your computer. You’ll map the address columns before the lookup begins.
                </Typography>
                <Button
                  className={classes.primaryButton}
                  color="primary"
                  variant="contained"
                  component="label"
                  disabled={isSubmitting}
                >
                  <CloudUploadRoundedIcon />
                  Choose CSV file
                  <input type="file" accept=".csv,text/csv" hidden onChange={onFileSelected} />
                </Button>
                <Typography component="p" className={classes.uploadMeta}>CSV format · Up to 15 MB</Typography>
              </div>
            ) : (
              <>
                <div className={classes.fileCard}>
                  <span className={classes.fileIcon} aria-hidden="true"><DescriptionRoundedIcon /></span>
                  <div className={classes.fileDetails}>
                    <div className={classes.fileName}>{file.name}</div>
                    <div className={classes.fileMeta}>
                      {`${(file.size / 1024).toFixed(1)} KB · ${headers.length} columns detected`}
                    </div>
                  </div>
                  <Button
                    className={classes.replaceButton}
                    color="primary"
                    component="label"
                    disabled={isSubmitting}
                  >
                    Replace file
                    <input type="file" accept=".csv,text/csv" hidden onChange={onFileSelected} />
                  </Button>
                </div>

                <div className={classes.mappingGrid}>
                  <section className={classes.mappingSection} aria-labelledby="property-fields-heading">
                    <div className={classes.mappingSectionHeader}>
                      <span className={classes.mappingSectionIcon} aria-hidden="true"><HomeWorkRoundedIcon /></span>
                      <div>
                        <Typography id="property-fields-heading" component="h3" className={classes.mappingSectionTitle}>
                          Property address
                        </Typography>
                        <Typography component="p" className={classes.mappingSectionDescription}>
                          Required fields used to locate each parcel.
                        </Typography>
                      </div>
                    </div>
                    <div className={classes.fieldsGrid}>
                      {mappingFields.filter(({ required }) => required).map((field) => (
                        <FormControl className={classes.fieldControl} variant="outlined" size="small" required key={field.key}>
                          <InputLabel id={`${field.key}-mapping-label`}>{field.label}</InputLabel>
                          <Select
                            labelId={`${field.key}-mapping-label`}
                            id={`${field.key}-mapping`}
                            value={mapping[field.key] ?? ''}
                            onChange={(event) => setMapping({ ...mapping, [field.key]: event.target.value as string })}
                            label={field.label}
                          >
                            <MenuItem value=""><em>Select a CSV column</em></MenuItem>
                            {headers.map((header) => <MenuItem key={header} value={header}>{header}</MenuItem>)}
                          </Select>
                        </FormControl>
                      ))}
                    </div>
                  </section>

                  <section className={classes.mappingSection} aria-labelledby="contact-fields-heading">
                    <div className={classes.mappingSectionHeader}>
                      <span className={classes.mappingSectionIcon} aria-hidden="true"><PeopleAltRoundedIcon /></span>
                      <div>
                        <Typography id="contact-fields-heading" component="h3" className={classes.mappingSectionTitle}>
                          Contact details
                        </Typography>
                        <Typography component="p" className={classes.mappingSectionDescription}>
                          Optional columns to preserve in the matched output.
                        </Typography>
                      </div>
                    </div>
                    <div className={classes.fieldsGrid}>
                      {mappingFields.filter(({ required }) => !required).map((field) => (
                        <FormControl className={classes.fieldControl} variant="outlined" size="small" key={field.key}>
                          <InputLabel id={`${field.key}-mapping-label`}>{field.label}</InputLabel>
                          <Select
                            labelId={`${field.key}-mapping-label`}
                            id={`${field.key}-mapping`}
                            value={mapping[field.key] ?? ''}
                            onChange={(event) => setMapping({ ...mapping, [field.key]: event.target.value as string || undefined })}
                            label={field.label}
                          >
                            <MenuItem value=""><em>Do not include</em></MenuItem>
                            {headers.map((header) => <MenuItem key={header} value={header}>{header}</MenuItem>)}
                          </Select>
                        </FormControl>
                      ))}
                    </div>
                  </section>
                </div>

                <div className={classes.actions}>
                  <div className={classes.actionButtons}>
                    <Button
                      className={classes.primaryButton}
                      color="primary"
                      variant="contained"
                      type="submit"
                      disabled={isSubmitting || !requiredMappingsComplete}
                    >
                      {isSubmitting ? 'Submitting…' : 'Start APN lookup'}
                    </Button>
                    <Button className={classes.clearButton} disabled={isSubmitting} onClick={resetUpload}>Clear</Button>
                  </div>
                  {isSubmitting && (
                    <div className={classes.uploadProgress} aria-live="polite">
                      <div className={classes.progressLabel}>
                        <span>Uploading CSV</span>
                        <span>{`${uploadProgress}%`}</span>
                      </div>
                      <LinearProgress variant="determinate" value={uploadProgress} />
                    </div>
                  )}
                </div>
              </>
            )}
          </form>
        </CardContent>
      </Card>

      <Card variant="outlined" className={classes.card}>
        <LookupCardHeader
          icon={<HistoryRoundedIcon />}
          title="Lookup History"
          description="Jobs continue in the background and active results update automatically"
          action={pagination?.total ? (
            <span className={classes.historyCount}>{`${pagination.total.toLocaleString()} total`}</span>
          ) : undefined}
        />
        {isLoading ? (
          <CardContent className={classes.cardBody}>
            <Box display="flex" justifyContent="center" p={4}><CircularProgress aria-label="Loading lookup history" /></Box>
          </CardContent>
        ) : dataLookups?.length ? (
          <CardContent className={classes.historyBody}>
            <TableContainer className={classes.tableContainer}>
              <Table aria-label="APN lookup history">
                <TableHead>
                  <TableRow>
                    <TableCell>Input file</TableCell>
                    <TableCell>Submitted by</TableCell>
                    <TableCell>Submitted</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Progress / Results</TableCell>
                    <TableCell>Downloads</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dataLookups.map((lookup) => {
                    const isCompleted = lookup.status === EDataLookupStatus.Completed;
                    const progress = lookup.inputRecordCount
                      ? Math.round((lookup.processedRecordCount / lookup.inputRecordCount) * 100)
                      : 0;
                    return (
                      <TableRow key={lookup._id}>
                        <TableCell>
                          <span className={classes.inputFileCell}>
                            <DescriptionRoundedIcon aria-hidden="true" />
                            {lookup.inputFiles[0]?.filename ?? 'CSV upload'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {[lookup.submittedBy.firstName, lookup.submittedBy.lastName].filter(Boolean).join(' ')
                              || lookup.submittedBy.email
                              || 'Unknown user'}
                          </Typography>
                          {(lookup.submittedBy.firstName || lookup.submittedBy.lastName) && lookup.submittedBy.email && (
                            <Typography variant="caption" color="textSecondary">{lookup.submittedBy.email}</Typography>
                          )}
                        </TableCell>
                        <TableCell>{moment(lookup.createdAt).format('MMM D, YYYY h:mm A')}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={statusLabels[lookup.status]}
                            className={`${classes.statusChip} ${statusClass(lookup.status)}`}
                          />
                        </TableCell>
                        <TableCell className={classes.progressCell}>
                          {isCompleted ? (
                            <>
                              <Typography variant="body2">{`${lookup.outputRecordCount.toLocaleString()} matched`}</Typography>
                              <Typography variant="caption" color="textSecondary">{`${lookup.failedRecordCount.toLocaleString()} errors`}</Typography>
                            </>
                          ) : (
                            <>
                              <Typography variant="body2">
                                {lookup.status === EDataLookupStatus.Failed
                                  ? 'Lookup failed'
                                  : `${lookup.processedRecordCount.toLocaleString()} / ${lookup.inputRecordCount.toLocaleString()}`}
                              </Typography>
                              {lookup.status === EDataLookupStatus.Failed && (
                                <Typography variant="caption" color="textSecondary">
                                  {`${lookup.processedRecordCount.toLocaleString()} / ${lookup.inputRecordCount.toLocaleString()} processed`}
                                </Typography>
                              )}
                              {lookup.status === EDataLookupStatus.Processing && (
                                <LinearProgress className={classes.rowProgress} variant="determinate" value={progress} />
                              )}
                            </>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className={classes.downloadActions}>
                            {[...lookup.outputFiles]
                              .sort((left, right) => Number(isAuditFile(left.kind)) - Number(isAuditFile(right.kind)))
                              .map((outputFile) => (
                                <Button
                                  key={outputFile._id}
                                  size="small"
                                  variant="outlined"
                                  startIcon={<GetAppRoundedIcon />}
                                  onClick={() => onDownload(lookup, outputFile)}
                                  aria-label={`Download ${isAuditFile(outputFile.kind) ? 'audit' : 'results'} CSV`}
                                >
                                  {isAuditFile(outputFile.kind) ? 'Audit' : 'Results'}
                                </Button>
                              ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              className={classes.pagination}
              component="div"
              count={pagination?.total ?? 0}
              page={page}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
              onChangePage={(_, nextPage) => setPage(nextPage)}
              onPageChange={(_, nextPage) => setPage(nextPage)}
              onChangeRowsPerPage={(event) => {
                setRowsPerPage(Number.parseInt(event.target.value, 10));
                setPage(0);
              }}
            />
          </CardContent>
        ) : (
          <CardContent className={classes.emptyState}>
            <span className={classes.emptyIcon} aria-hidden="true"><SearchRoundedIcon /></span>
            <Typography component="h3" className={classes.emptyTitle}>No lookups yet</Typography>
            <Typography component="p" className={classes.emptyDescription}>
              No APN lookups have been submitted across the platform yet.
            </Typography>
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default APNLookupView;
